import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';

const CATEGORIES = [
  'General Announcement', 'School Circular', 'Holiday Notice',
  'Leave Notification', 'Fee Notice', 'Meeting Notice',
  'Policy Update', 'Event Update', 'Exam Notification', 'Other',
];
const AUDIENCES = [
  'All Staff & Parents', 'All Staff', 'Teaching Staff Only',
  'Administrative Staff', 'All Parents', 'Management Only', 'Other',
];
const PRIORITIES = ['Normal', 'Important', 'Urgent'];

const PRIORITY_COLORS = { Normal: '#6c757d', Important: '#f59e0b', Urgent: '#ef4444' };
const CAT_ICONS = {
  'General Announcement': 'bi-megaphone',
  'School Circular': 'bi-file-earmark-text',
  'Holiday Notice': 'bi-calendar-x',
  'Leave Notification': 'bi-person-check',
  'Fee Notice': 'bi-cash-coin',
  'Meeting Notice': 'bi-people',
  'Policy Update': 'bi-shield-check',
  'Event Update': 'bi-calendar-event',
  'Exam Notification': 'bi-journal-check',
  'Other': 'bi-chat-square-text',
};

const blank = {
  title: '', body: '', category: 'General Announcement', priority: 'Normal',
  audience: 'All Staff & Parents', audienceCustom: '', pinned: false,
  effective_date: '', expiry_date: '', reference_number: '',
};

export default function ManagementCommunication({ moduleKey = 'management_communication' }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'manager', 'hr', 'principal'].includes(user?.roleKey);

  const [messages,    setMessages]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [form,        setForm]        = useState(blank);
  const [otherAud,    setOtherAud]    = useState('');
  const [otherCat,    setOtherCat]    = useState('');
  const [saving,      setSaving]      = useState(false);
  const [filterCat,   setFilterCat]   = useState('');
  const [expandedId,  setExpandedId]  = useState(null);
  const [activeTab,   setActiveTab]   = useState('all');
  const [toast,       setToast]       = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get(`/records/${moduleKey}`, { params: { limit: 100 } })
      .catch(() => ({ data: { records: [] } }));
    let msgs = r.data.records || [];
    msgs.sort((a, b) => {
      if (a.recordData?.pinned && !b.recordData?.pinned) return -1;
      if (!a.recordData?.pinned && b.recordData?.pinned) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    setMessages(msgs);
    setLoading(false);
  }, [moduleKey]);

  useEffect(() => { load(); }, [load]);

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = {
      ...form,
      audience: form.audience === 'Other' ? otherAud : form.audience,
      category: form.category === 'Other' ? otherCat : form.category,
    };
    try {
      if (editing?._id) await api.put(`/records/${moduleKey}/${editing._id}`, payload);
      else              await api.post(`/records/${moduleKey}`, payload);
      setToast({ show: true, message: `Notice ${editing ? 'updated' : 'posted'}!`, type: 'success' });
      setShowForm(false); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    await api.delete(`/records/${moduleKey}/${id}`).catch(() => {});
    load();
  };

  const openEdit = (r) => {
    const d = r.recordData || {};
    setForm({ ...blank, ...d });
    setOtherAud(!AUDIENCES.includes(d.audience) && d.audience ? d.audience : '');
    setOtherCat(!CATEGORIES.includes(d.category) && d.category ? d.category : '');
    setEditing(r); setShowForm(true);
  };

  const openNew = () => {
    setForm(blank); setEditing(null); setOtherAud(''); setOtherCat('');
    setShowForm(true);
  };

  // Tab filter
  const TAB_FILTERS = {
    all:     () => true,
    pinned:  m => m.recordData?.pinned,
    leave:   m => m.recordData?.category === 'Leave Notification',
    notice:  m => ['School Circular', 'General Announcement', 'Policy Update'].includes(m.recordData?.category),
    urgent:  m => m.recordData?.priority === 'Urgent',
  };

  const filtered = messages.filter(m =>
    TAB_FILTERS[activeTab]?.(m) !== false &&
    (!filterCat || m.recordData?.category === filterCat)
  );

  const urgentCount = messages.filter(m => m.recordData?.priority === 'Urgent' && !m.recordData?.pinned).length;

  return (
    <div>
      <PageHeader
        title="Management Communication"
        icon="bi-chat-square-text-fill"
        subtitle="Notices, circulars and announcements for staff & parents"
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width: 180 }}
              value={filterCat} onChange={e => setFilterCat(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.filter(c => c !== 'Other').map(c => <option key={c}>{c}</option>)}
            </select>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                <i className="bi bi-megaphone me-1"></i>Post Notice
              </button>
            )}
          </div>
        }
      />

      {/* Stats row */}
      <div className="row g-2 mb-3">
        {[
          { label: 'Total Notices', count: messages.length,        color: '#6366f1', bg: '#ede9fe', icon: 'bi-megaphone' },
          { label: 'Pinned',        count: messages.filter(m => m.recordData?.pinned).length, color: '#f59e0b', bg: '#fef3c7', icon: 'bi-pin-fill' },
          { label: 'Urgent',        count: urgentCount,            color: '#ef4444', bg: '#fee2e2', icon: 'bi-exclamation-circle-fill' },
          { label: 'Leave Notices', count: messages.filter(m => m.recordData?.category === 'Leave Notification').length, color: '#0ea5e9', bg: '#e0f2fe', icon: 'bi-person-check' },
        ].map(s => (
          <div className="col-6 col-md-3" key={s.label}>
            <div className="card text-center py-2" style={{ border: `1px solid ${s.bg}`, background: s.bg }}>
              <div className="d-flex align-items-center justify-content-center gap-2">
                <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: 18 }}></i>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'all',    label: 'All',           icon: 'bi-list-ul' },
          { key: 'pinned', label: 'Pinned',         icon: 'bi-pin-fill' },
          { key: 'leave',  label: 'Leave Notices',  icon: 'bi-person-check' },
          { key: 'notice', label: 'Circulars',      icon: 'bi-file-earmark-text' },
          { key: 'urgent', label: 'Urgent',         icon: 'bi-exclamation-triangle-fill' },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link ${activeTab === t.key ? 'active fw-semibold' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.key === 'urgent' && urgentCount > 0 && (
                <span className="badge bg-danger ms-1" style={{ fontSize: 9 }}>{urgentCount}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* Compose form */}
      {showForm && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center"
            style={{ background: '#f5f3ff' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-megaphone me-2 text-primary"></i>
              {editing ? 'Edit Notice' : 'Post New Notice / Circular'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label fw-semibold">Title / Subject <span className="text-danger">*</span></label>
                  <input className="form-control" required value={form.title}
                    onChange={e => sf('title')(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Category</label>
                  <select className="form-select" value={form.category}
                    onChange={e => sf('category')(e.target.value)}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                  {form.category === 'Other' && (
                    <input className="form-control mt-1" placeholder="Specify…"
                      value={otherCat} onChange={e => setOtherCat(e.target.value)} autoFocus />
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Priority</label>
                  <select className="form-select" value={form.priority}
                    onChange={e => sf('priority')(e.target.value)}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-md-2 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="pinned"
                      checked={!!form.pinned} onChange={e => sf('pinned')(e.target.checked)} />
                    <label className="form-check-label" htmlFor="pinned" style={{ fontSize: 13 }}>
                      Pin to top
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Audience</label>
                  <select className="form-select" value={form.audience}
                    onChange={e => sf('audience')(e.target.value)}>
                    {AUDIENCES.map(a => <option key={a}>{a}</option>)}
                  </select>
                  {form.audience === 'Other' && (
                    <input className="form-control mt-1" placeholder="Specify audience…"
                      value={otherAud} onChange={e => setOtherAud(e.target.value)} autoFocus />
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Effective Date</label>
                  <input type="date" className="form-control" value={form.effective_date || ''}
                    onChange={e => sf('effective_date')(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Expiry Date</label>
                  <input type="date" className="form-control" value={form.expiry_date || ''}
                    onChange={e => sf('expiry_date')(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Reference No. <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>(optional)</span></label>
                  <input className="form-control" placeholder="e.g. CIRC/2025/001"
                    value={form.reference_number || ''} onChange={e => sf('reference_number')(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Message Body <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows={6} required
                    value={form.body} onChange={e => sf('body')(e.target.value)}
                    placeholder="Write the full notice/circular content here…" />
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3 pt-2 border-top">
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Notice' : <><i className="bi bi-megaphone me-1"></i>Post Notice</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notice Feed */}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-5 text-muted">
          <i className="bi bi-chat-square-text display-4 d-block mb-2 opacity-25"></i>
          No notices found.{isAdmin && <> <button className="btn btn-link p-0" onClick={openNew}>Post the first notice</button></>}
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {filtered.map(m => {
            const d = m.recordData || {};
            const isExpanded = expandedId === m._id;
            const pc = PRIORITY_COLORS[d.priority] || '#6c757d';
            const catIcon = CAT_ICONS[d.category] || 'bi-megaphone';
            const isExpired = d.expiry_date && new Date(d.expiry_date) < new Date();
            return (
              <div key={m._id} className="card"
                style={{ borderRadius: 12, borderLeft: `4px solid ${pc}`, opacity: isExpired ? 0.65 : 1 }}>
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        {d.pinned && <i className="bi bi-pin-fill text-warning" title="Pinned"></i>}
                        <i className={`bi ${catIcon}`} style={{ color: pc, fontSize: 14 }}></i>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{d.title}</span>
                        <span className="badge"
                          style={{ background: pc + '20', color: pc, fontSize: 10 }}>
                          {d.priority}
                        </span>
                        <span className="badge bg-light text-dark" style={{ fontSize: 10 }}>
                          {d.category}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          <i className="bi bi-people me-1"></i>{d.audience}
                        </span>
                        {isExpired && (
                          <span className="badge bg-secondary" style={{ fontSize: 9 }}>Expired</span>
                        )}
                      </div>
                      <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 11, color: '#94a3b8' }}>
                        <span>
                          <i className="bi bi-calendar me-1"></i>
                          Posted {new Date(m.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {d.effective_date && (
                          <span><i className="bi bi-calendar-check me-1 text-success"></i>Effective: {new Date(d.effective_date).toLocaleDateString()}</span>
                        )}
                        {d.expiry_date && (
                          <span><i className="bi bi-calendar-x me-1 text-danger"></i>Expires: {new Date(d.expiry_date).toLocaleDateString()}</span>
                        )}
                        {d.reference_number && (
                          <span><i className="bi bi-hash me-1"></i>{d.reference_number}</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0">
                      <button className="btn btn-outline-secondary btn-sm"
                        style={{ fontSize: 11, padding: '2px 8px' }}
                        onClick={() => setExpandedId(isExpanded ? null : m._id)}>
                        {isExpanded ? 'Collapse' : 'Read'}
                      </button>
                      {isAdmin && (
                        <>
                          <button className="btn btn-outline-primary btn-sm"
                            style={{ padding: '2px 6px' }} onClick={() => openEdit(m)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-outline-danger btn-sm"
                            style={{ padding: '2px 6px' }} onClick={() => handleDelete(m._id)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {!isExpanded && d.body && (
                    <div style={{
                      fontSize: 13, color: '#64748b', marginTop: 8,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {d.body}
                    </div>
                  )}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-top"
                      style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#334155' }}>
                      {d.body}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
